import React, { useCallback, useMemo } from 'react';

import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Show } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getConfluenceUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { is3pProductKeyForAnalytics } from '../../../../common/constants/products';
import { EventTypeMessage, VIEWED_EVENT } from '../../../../common/ui/event-type-message';
import { getConfluenceEntityIcon } from '../../../../common/ui/product-entity-icon';
import { useItemsAnalyticsContext } from '../../../../common/ui/quick-find/items-analytics-provider';
import {
	SearchResult,
	type SearchResultEventArguments,
} from '../../../../common/ui/quick-find/search-dialog-result';
import { type EnrichedRecommendedObject } from '../../../../common/ui/quick-find/types';
import { type SearchMetadataPart } from '../../../../common/ui/search-metadata';
import { type State } from '../../../../common/utils/quick-find/state';
import {
	getResultConnectorSource,
	isResultSmartLink,
} from '../../../../common/utils/third-party-utils';
import { useLogException } from '../../../../controllers/error-boundary/utils';
import { type QuickFindResultEvent } from '../../../../controllers/quick-find/utils';
import { useSearchSessionId, useThirdPartyConfigs } from '../../../../controllers/store/bootstrap';
import { useQuickFindQuery } from '../../../../controllers/store/quick-find';

import { UserDocumentEventTypeMessage } from './event-type-message';
import { resolveDisplayTimestamp } from './event-type-message/utils';
import { messages } from './messages';

const styles = cssMap({
	titleContainer: {
		display: 'inline-flex',
		gap: token('space.075'),
		alignItems: 'center',
		maxWidth: '100%',
	},
	textOverflow: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	rightSection: {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: token('space.075'),
	},
});

type TitleWithRightMetadata = {
	title: string;
	rightMetadata?: React.JSX.Element | null;
};

const TitleWithRightMetadata = (props: TitleWithRightMetadata) => {
	const { title, rightMetadata } = props;
	return (
		<Box xcss={styles.titleContainer}>
			<Box xcss={styles.textOverflow}>{title}</Box>
			<Box xcss={styles.rightSection}>
				<Show above="xs">{rightMetadata}</Show>
			</Box>
		</Box>
	);
};

type Reason = {
	type: string;
	feature: string;
	timestamp: string;
	text: string;
	metadata: {
		collaborators: string[];
	};
};

const mapConfluenceSearchResult = (
	result: EnrichedRecommendedObject,
	query: string,
	searchSessionId: string,
	logException: (error: Error) => Promise<void>,
	serveNewUI: boolean,
	intl: IntlShape,
) => {
	const id = result.id;

	const title = result?.object?.title;
	if (!title) {
		return null;
	}

	const space = result?.object?.space?.name ? result.object.space.name : null;
	const spaceId = result?.object?.space?.id;

	if (!space || !spaceId) {
		return null;
	}

	const rawUrl =
		result?.object?.links?.base && result.object.links?.webUi
			? result?.object?.links?.base + result.object.links?.webUi
			: undefined;

	if (!rawUrl) {
		return null;
	}

	const url = getConfluenceUrlWithAnalyticsQueryParams({
		url: rawUrl,
		query,
		searchSessionId,
	});

	let rightMetadata: React.ReactNode = null;

	if (result.reason && fg('quick_find_recommendations_decoupling')) {
		rightMetadata = (
			<UserDocumentEventTypeMessage
				reason={result.reason}
				activityEnrichment={result.activityEnrichment}
				intl={intl}
				logException={logException}
			/>
		);
	} else {
		let reason: Reason | undefined;

		try {
			reason = result.reason ? (JSON.parse(result.reason) as Reason) : undefined;
		} catch (error) {
			logException(error as Error);
		}

		const displayTimestamp = reason
			? resolveDisplayTimestamp(
					reason?.type,
					VIEWED_EVENT.VIEWED, // legacy path always treats user documents as viewed
					reason?.timestamp,
					result.activityEnrichment,
				)
			: undefined;

		rightMetadata =
			reason && displayTimestamp ? (
				<EventTypeMessage
					timestamp={displayTimestamp}
					eventType={VIEWED_EVENT.VIEWED}
					isMyActivityData={reason.type === 'no_collaborator'}
				/>
			) : null;
	}

	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const isLiveDoc =
		result?.object?.__typename === 'ConfluencePage' && result.object.subtype === 'LIVE';

	const icon = getConfluenceEntityIcon({
		iconUrl: undefined,
		type: result.object?.type?.replace('_', '').toLowerCase() || '',
		isLiveDoc,
	});

	const metadata = [
		[
			'type',
			isLiveDoc ? (
				<FormattedMessage {...messages.liveDoc} />
			) : result?.object?.type === 'ATTACHMENT' ? (
				<FormattedMessage {...messages.attachment} />
			) : result?.object?.type === 'BLOG_POST' ? (
				<FormattedMessage {...messages.blogpost} />
			) : result?.object?.type === 'COMMENT' ? (
				<FormattedMessage {...messages.comment} />
			) : result?.object?.type === 'PAGE' ? (
				<FormattedMessage {...messages.page} />
			) : result?.object?.type === 'WHITEBOARD' ? (
				<FormattedMessage {...messages.whiteboard} />
			) : null,
		],
		space,
	] satisfies SearchMetadataPart[];

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		containerId: spaceId,
		contentType: `confluence ${result?.object?.type}`,
		product: `confluence`,
	};

	if (serveNewUI) {
		const titleWithRightMetadata = (
			<TitleWithRightMetadata title={title} rightMetadata={rightMetadata} />
		);

		return {
			id,
			icon,
			title: titleWithRightMetadata,
			highlightedTitle,
			highlightedRightMetadata,
			url,
			analyticsMetadata,
		};
	}

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapSearchResult = (
	result: EnrichedRecommendedObject,
	query: string,
	searchSessionId: string,
	logException: (error: Error) => Promise<void>,
	serveNewUI: boolean,
	intl: IntlShape,
) => {
	if (
		result?.object?.__typename === 'ConfluencePage' ||
		result?.object?.__typename === 'ConfluenceBlogPost'
	) {
		return mapConfluenceSearchResult(
			result,
			query,
			searchSessionId,
			logException,
			serveNewUI,
			intl,
		);
	} else {
		return null;
	}
};

type Props = {
	result: EnrichedRecommendedObject;
	state: State;
	onClick?: (props: QuickFindResultEvent) => void;
	onContextMenu?: (props: QuickFindResultEvent) => void;
	onHighlighted?: (props: QuickFindResultEvent) => void;
};

export const UserDocument = ({
	result,
	state,
	onClick,
	onContextMenu,
	onHighlighted,
}: Props): React.JSX.Element | null => {
	const quickFindQuery = useQuickFindQuery();
	const [searchSessionId] = useSearchSessionId();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const { invalidateItem } = useItemsAnalyticsContext();
	const logException = useLogException();
	const intl = useIntl();

	const serveNewUI = state === 'PREQUERY_RESULTS' && fg('quick_find_recommendations_new_ui');

	const mappedResult = mapSearchResult(
		result,
		quickFindQuery,
		searchSessionId,
		logException,
		serveNewUI,
		intl,
	);

	const additionalAnalyticsAttributes = useMemo(
		() => ({
			metadata: {
				...mappedResult?.analyticsMetadata,
				connectorType: getResultConnectorSource({
					// We cannot provide a value for connectorType here because the user relevant documents API
					// does not return a connectorType for a given entity.
					resultNodeId: result.id,
					thirdPartyConfigs,
					productKey: mappedResult?.analyticsMetadata.product,
				}),
			},
			is3pLink: is3pProductKeyForAnalytics(mappedResult?.analyticsMetadata.product),
			is3pSmartLink: isResultSmartLink({
				resultAri: result.id,
				thirdPartyConfigs,
				productKey: mappedResult?.analyticsMetadata.product,
			}),
		}),
		[mappedResult, result.id, thirdPartyConfigs],
	);

	const onResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onClick?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onClick, state],
	);

	const onResultContextMenu = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onContextMenu?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onContextMenu, state],
	);

	const onResultHighlighted = useCallback(
		(data: SearchResultEventArguments) => {
			onHighlighted?.({
				data,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onHighlighted, state],
	);

	if (!mappedResult) {
		invalidateItem(result.id);
		return null;
	}

	return (
		<SearchResult
			{...mappedResult}
			resultType="recommendations"
			product={mappedResult.analyticsMetadata.product}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onResultHighlighted={onResultHighlighted}
		/>
	);
};
